import { useEffect, useMemo, useState, useCallback } from "react";
import { startOfWeek, addDays, addWeeks, format, isSameDay } from "date-fns";
import { toast } from "sonner";
import { Plus, ChevronLeft, ChevronRight, Trash2, CalendarPlus, ListPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { MEAL_TYPES, MEAL_ICONS } from "@/lib/constants";
import { PageHeader } from "@/components/shared";
import { cn } from "@/lib/utils";

export default function Meals() {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 0 }));
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ date: "", meal_type: "dinner", title: "", ingredients: "" });
  const [saving, setSaving] = useState(false);

  const weekOf = format(weekStart, "yyyy-MM-dd");
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setMeals(await api(`/meals?week_of=${weekOf}`));
    } finally {
      setLoading(false);
    }
  }, [weekOf]);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = (day) => {
    setForm({ date: format(day, "yyyy-MM-dd"), meal_type: "dinner", title: "", ingredients: "" });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Add a meal name");
    setSaving(true);
    try {
      await api("/meals", {
        method: "POST",
        body: {
          date: form.date,
          meal_type: form.meal_type,
          title: form.title.trim(),
          notes: "",
          ingredients: form.ingredients
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
        },
      });
      setDialogOpen(false);
      await load();
      toast.success("Meal planned");
    } catch {
      toast.error("Could not save meal");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (m) => {
    setMeals((prev) => prev.filter((x) => x.id !== m.id));
    try {
      await api(`/meals/${m.id}`, { method: "DELETE" });
    } catch {
      load();
    }
  };

  const toCalendar = async (m) => {
    try {
      await api(`/meals/${m.id}/to-calendar`, { method: "POST" });
      toast.success("Added to calendar");
    } catch {
      toast.error("Could not add to calendar");
    }
  };

  const generateList = async () => {
    try {
      const res = await api(`/meals/generate-shopping-list?week_of=${weekOf}`, { method: "POST" });
      toast.success(res.added > 0 ? `Added ${res.added} items to shopping` : "Nothing new to add");
    } catch {
      toast.error("Could not generate list");
    }
  };

  const mealsFor = (day) => {
    const ds = format(day, "yyyy-MM-dd");
    const list = meals.filter((m) => m.date === ds);
    return MEAL_TYPES.flatMap((t) => list.filter((m) => m.meal_type === t));
  };

  return (
    <div>
      <PageHeader
        title="Meals"
        subtitle="Plan the week's menu"
        action={
          <Button variant="outline" className="rounded-full" onClick={generateList} data-testid="generate-list-btn">
            <ListPlus className="mr-1.5 h-4 w-4" /> To shopping list
          </Button>
        }
      />

      <div className="mt-4 flex items-center justify-between">
        <Button variant="ghost" size="icon" className="rounded-full" onClick={() => setWeekStart((w) => addWeeks(w, -1))} data-testid="week-prev-btn">
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <span className="font-display text-base font-semibold text-ink">
          {format(weekStart, "MMM d")} – {format(addDays(weekStart, 6), "MMM d")}
        </span>
        <Button variant="ghost" size="icon" className="rounded-full" onClick={() => setWeekStart((w) => addWeeks(w, 1))} data-testid="week-next-btn">
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {loading ? (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {days.map((d) => (
            <Skeleton key={d.toISOString()} className="h-40 rounded-3xl" />
          ))}
        </div>
      ) : (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {days.map((day) => {
            const dayMeals = mealsFor(day);
            const today = isSameDay(day, new Date());
            return (
              <div key={day.toISOString()} className="bento-tile flex flex-col p-4" data-testid={`meal-day-${format(day, "yyyy-MM-dd")}`}>
                <div className="mb-2 flex items-center justify-between">
                  <div>
                    <p className={cn("font-display text-sm font-semibold", today ? "text-primary" : "text-ink")}>
                      {format(day, "EEEE")}
                    </p>
                    <p className="text-xs text-muted-foreground">{format(day, "MMM d")}</p>
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={() => openAdd(day)} data-testid={`meal-add-${format(day, "yyyy-MM-dd")}`}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex-1 space-y-2">
                  {dayMeals.length === 0 ? (
                    <p className="py-4 text-center text-xs text-muted-foreground/70">No meals yet</p>
                  ) : (
                    dayMeals.map((m) => (
                      <div key={m.id} className="group flex items-start gap-2 rounded-xl bg-secondary/50 px-3 py-2" data-testid={`meal-item-${m.id}`}>
                        <span className="text-lg leading-none">{MEAL_ICONS[m.meal_type]}</span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-ink">{m.title}</p>
                          <p className="text-[11px] capitalize text-muted-foreground">{m.meal_type}</p>
                        </div>
                        <button onClick={() => toCalendar(m)} title="Add to calendar" className="text-muted-foreground/60 hover:text-sky" data-testid={`meal-tocal-${m.id}`}>
                          <CalendarPlus className="h-4 w-4" />
                        </button>
                        <button onClick={() => remove(m)} title="Delete" className="text-muted-foreground/60 hover:text-destructive" data-testid={`meal-delete-${m.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-display">Plan a meal</DialogTitle>
            <DialogDescription className="sr-only">Meal type, name and ingredients</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Meal type</Label>
              <Select value={form.meal_type} onValueChange={(v) => setForm({ ...form, meal_type: v })}>
                <SelectTrigger className="rounded-xl" data-testid="meal-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MEAL_TYPES.map((t) => (
                    <SelectItem key={t} value={t} className="capitalize">
                      {MEAL_ICONS[t]} {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>What&apos;s cooking?</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Spaghetti bolognese"
                className="rounded-xl"
                data-testid="meal-title-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Ingredients (comma separated)</Label>
              <Textarea
                value={form.ingredients}
                onChange={(e) => setForm({ ...form, ingredients: e.target.value })}
                placeholder="Pasta, tomatoes, beef, onion"
                className="rounded-xl"
                data-testid="meal-ingredients-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button className="rounded-xl" onClick={save} disabled={saving} data-testid="meal-save-btn">
              {saving ? "Saving…" : "Save meal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
